import config from "../config";
import { getSessionToken } from "./helpers";

let onUnauthorized = () => {};

export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

const publicRequest = { authenticate: false };

export const userLogin = (email, password) =>
  requestApi("/users/login", "POST", { email, password }, publicRequest);

export const lookupInvitation = async (token) => {
  const { invitation } = await requestApi("/invitations/lookup", "POST", { token }, publicRequest);
  return invitation;
};

export const acceptInvitation = (token, password) =>
  requestApi("/invitations/accept", "POST", { token, password }, publicRequest);

export const acceptPasswordReset = (token, password) =>
  requestApi("/passwordResets/accept", "POST", { token, password }, publicRequest);

export const getMe = async () => {
  const { user } = await requestApi("/me");
  return user;
};

export const changePassword = (currentPassword, newPassword) =>
  requestApi("/me/password", "POST", { currentPassword, newPassword });

export const getOrganizations = async () => {
  const { organizations } = await requestApi("/organizations");
  return organizations;
};

export const createOrganization = async (name) => {
  const { organization } = await requestApi("/organizations", "POST", { name });
  return organization;
};

export const getMembers = (organization) => requestApi(`/organizations/${organization}/members`);

export const createInvitation = (organization, email) =>
  requestApi(`/organizations/${organization}/invitations`, "POST", { email });

export const revokeInvitation = (organization, id) =>
  requestApi(`/organizations/${organization}/invitations/${id}`, "DELETE");

export const createPasswordResetLink = async (organization, memberId) => {
  const { token } = await requestApi(
    `/organizations/${organization}/members/${memberId}/passwordReset`,
    "POST",
  );
  return token;
};

export const removeMember = (organization, memberId) =>
  requestApi(`/organizations/${organization}/members/${memberId}`, "DELETE");

export const getBrickSeriesForOrganization = async (organization) => {
  const { brickSeries } = await requestApi(`/organizations/${organization}/brickSeries`);
  return brickSeries;
};

export const getBrickSeries = async (organization, series) => {
  const { brickSeries } = await requestApi(
    `/organizations/${organization}/brickSeries/${series}`,
  );
  return brickSeries;
};

export const createBrickSeries = async (organization, params) => {
  const { brickSeries } = await requestApi(
    `/organizations/${organization}/brickSeries`,
    "POST",
    params,
  );
  return brickSeries;
};

export const updateBrickSeries = async (organization, id, params) => {
  const { brickSeries } = await requestApi(
    `/organizations/${organization}/brickSeries/${id}`,
    "POST",
    params,
  );
  return brickSeries;
};

export const deleteBrickSeries = (organization, id) =>
  requestApi(`/organizations/${organization}/brickSeries/${id}`, "DELETE");

export const requestUploads = async (organization, brickSeries, files) => {
  const response = await requestApi("/brickSeries/bricks/uploadRequest", "POST", {
    organization,
    brickSeries,
    files,
  });
  return response.files;
};

const masterImagesPath = (organization, series) =>
  `/organizations/${organization}/brickSeries/${series}/masterImages`;

export const createMasterImage = (organization, series, params) =>
  requestApi(masterImagesPath(organization, series), "POST", params);

export const saveMasterImageDraft = (organization, series, id, draft) =>
  requestApi(`${masterImagesPath(organization, series)}/${id}`, "POST", draft);

export const applyMasterImageRenders = async (organization, series, id, renders) => {
  const { brickSeries } = await requestApi(
    `${masterImagesPath(organization, series)}/${id}/apply`,
    "POST",
    { renders },
  );
  return brickSeries;
};

export const deleteMasterImage = async (organization, series, id) => {
  const { brickSeries } = await requestApi(
    `${masterImagesPath(organization, series)}/${id}`,
    "DELETE",
  );
  return brickSeries;
};

const httpError = (status, message) => Object.assign(new Error(message), { status });

export const requestApi = async (
  path = "",
  method = "GET",
  data = null,
  { authenticate = true } = {},
) => {
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  const headers = { "Content-Type": "application/json" };
  const token = authenticate ? getSessionToken() : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${config.domains.api}${path}`, {
    method: method.toUpperCase(),
    mode: "cors",
    cache: "no-cache",
    headers,
    body: data ? JSON.stringify(data) : null,
  });

  if (response.status === 401 && token) {
    onUnauthorized();
    throw httpError(401, "Je sessie is verlopen. Log opnieuw in.");
  }

  if (response.status < 200 || response.status >= 300) {
    const body = await response.json().catch(() => null);
    throw httpError(response.status, body?.error ?? `Verzoek mislukt (${response.status})`);
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
};
